import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteLetter,
  draftWord,
  emptyDraft,
  selectCell as selectCellIn,
  typeLetter,
  type DraftState,
} from "./boardDraft.ts";
import { gameEdition } from "../lib/edition.ts";
import { playSound } from "../lib/sound.ts";

interface Options {
  disabled: boolean;
  /** Tahta dolu değilse `word` null gelir; uyarıyı çağıran gösterir. */
  onSubmit: (word: string | null) => void;
}

/**
 * Aktif satırdaki yazım durumu + fiziksel klavye desteği.
 * Üç mod da (pratik, günlük, düello) bunu paylaşır.
 */
export function useBoardInput({ disabled, onSubmit }: Options) {
  const [draft, setDraftState] = useState<DraftState>(emptyDraft);

  /*
   * Durumu ayrıca ref'te tutuyoruz: gönderim bir state güncelleyicisinin
   * içinden okunursa React StrictMode güncelleyiciyi iki kez çağırdığı için
   * tahmin çift gidiyor ve oyuncunun bir hakkı boşa harcanıyordu.
   */
  const draftRef = useRef<DraftState>(draft);

  const apply = useCallback((next: DraftState) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  const press = useCallback(
    (key: string) => {
      if (disabled) return;
      if (key === "ENTER") {
        playSound("submit", { userGesture: true });
        onSubmit(draftWord(draftRef.current));
        return;
      }
      if (key === "BACKSPACE") {
        const next = deleteLetter(draftRef.current);
        if (next !== draftRef.current) playSound("delete", { userGesture: true });
        apply(next);
        return;
      }
      if (!gameEdition.alphabetSet.has(key)) return;
      const next = typeLetter(draftRef.current, key);
      if (next !== draftRef.current) playSound("letter", { userGesture: true });
      apply(next);
    },
    [apply, disabled, onSubmit],
  );

  /** Kutucuğa basmak onu seçer; aynı kutucuk seçimi bırakır. */
  const selectCell = useCallback(
    (index: number) => {
      if (disabled) return;
      apply(selectCellIn(draftRef.current, index));
    },
    [apply, disabled],
  );

  const clear = useCallback(() => apply(emptyDraft()), [apply]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === "Enter") {
        event.preventDefault();
        if (event.repeat) return;
        press("ENTER");
        return;
      }
      if (event.key === "Backspace") {
        event.preventDefault();
        press("BACKSPACE");
        return;
      }
      if (event.key === "Escape") {
        const pinned = draftRef.current.pinned;
        if (pinned !== null) {
          event.preventDefault();
          selectCell(pinned);
        }
        return;
      }
      if ([...event.key].length !== 1) return;
      const letter = gameEdition.normalizeWord(event.key);
      if (gameEdition.alphabetSet.has(letter)) {
        event.preventDefault();
        press(letter);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [press, selectCell]);

  return { cells: draft.cells, pinned: draft.pinned, press, selectCell, clear };
}
